import { TypedDocumentNode, gql } from '@apollo/client';
import { faCirclePlay } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { OnboardingStepDialog } from '@sorare/core/src/components/rivals/OnboardingStepDialog';
import {
  FOOTBALL_PLAY_RIVALS_ARENA,
  FOOTBALL_PLAY_RIVALS_DRAFT_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import {
  useFootballEventOnce,
  useFootballEvents,
} from '@sorare/gameplay/src/lib/events';

import { useFootballRivalsUpdateFavoriteTeam } from 'components/rivals/Onboarding/FavoriteClubSelection/useFootballRivalsUpdateFavoriteTeam';

import { LearnTheBasics } from './LearnTheBasics';
import { WelcomeToSorare } from './WelcomeToSorare';
import {
  ClubSelectionDialogQuery,
  ClubSelectionDialogQueryVariables,
} from './__generated__/index.graphql';

const CLUB_SELECTION_DIALOG_QUERY = gql`
  query ClubSelectionDialogQuery($clubSlug: String!) {
    football {
      club(slug: $clubSlug) {
        slug
        id
        styleGuideColor
        lastRivalsGame {
          slug
          id
          game {
            id
            date
            competition {
              slug
              name
            }
            homeTeam {
              slug
              id
            }
            awayTeam {
              slug
              id
            }
          }
          ...LearnTheBasics_footballRivalsGame
        }
        ...WelcomeToSorare_club
      }
    }
  }
  ${WelcomeToSorare.fragments.club}
  ${LearnTheBasics.fragments.footballRivalsGame}
` as TypedDocumentNode<
  ClubSelectionDialogQuery,
  ClubSelectionDialogQueryVariables
>;

type Props = { clubSlug: string | null; open?: boolean; onClose: () => void };
export const ClubSelectionDialog = ({ clubSlug, open, onClose }: Props) => {
  const navigate = useNavigate();
  const [loadingSkip, setLoadingSkip] = useState(false);
  const [loadingDone, setLoadingDone] = useState(false);
  const [mutate] = useFootballRivalsUpdateFavoriteTeam();
  const trackOnce = useFootballEventOnce();
  const track = useFootballEvents();

  const { data, previousData, loading } = useQuery(
    CLUB_SELECTION_DIALOG_QUERY,
    {
      variables: { clubSlug: clubSlug! },
      skip: !clubSlug,
    }
  );

  const fetchedData = data || previousData;

  if (loading) {
    return null;
  }

  if (open) {
    trackOnce('Viewed RivalsWelcomeScreen');
  }

  const { club } = fetchedData?.football || {};
  const { lastRivalsGame } = club || {};

  const onDone = async () => {
    if (club?.id) {
      setLoadingDone(true);
      await mutate({ variables: { input: { teamId: club.id } } });
      setLoadingDone(false);
    }

    navigate(
      `${generatePath(FOOTBALL_PLAY_RIVALS_DRAFT_SLUG, { slug: lastRivalsGame?.slug || '' })}?simulation=true`
    );
  };
  const onSkip = async () => {
    if (club?.id) {
      setLoadingSkip(true);
      await mutate({ variables: { input: { teamId: club.id } } });
      setLoadingSkip(false);
    }

    track('Skip Simulation');
    navigate(FOOTBALL_PLAY_RIVALS_ARENA);
  };

  return (
    <OnboardingStepDialog
      open={!!open}
      onDone={onDone}
      onSkip={onSkip}
      onClose={onClose}
      lastCtaLabel={
        <Horizontal>
          <FontAwesomeIcon icon={faCirclePlay} color="var(--c-white)" />
          <FormattedMessage
            id="ClubSelectionDialog.lastCtaLabel"
            defaultMessage="Start Replay"
          />
        </Horizontal>
      }
      loadingSkip={loadingSkip}
      loadingNext={loadingDone}
      onNext={() => trackOnce('Viewed RivalsStartReplayScreen')}
    >
      <WelcomeToSorare club={club} />
      <LearnTheBasics
        color={club?.styleGuideColor}
        rivalsGame={lastRivalsGame}
      />
    </OnboardingStepDialog>
  );
};
